import { useCallback, useState, useMemo } from 'react';
import { types } from '~/infra/api/rpc/_generated';
import { apiRoutesFetch } from '~/repositories/next-api-routes-repository';
import { useSystemNotification } from '@app/_hooks/use-system-notification';
import { reportStoryViolation } from '@app/_repositories/mutation-service';
import { ViolationType } from './types';

export const useLocalHooks = (storyId: string) => {
  const [isOpen, setOpen] = useState(false);
  const open = useCallback(() => {
    if (isOpen) return;
    setOpen(true);
  }, [isOpen]);
  const close = useCallback(() => setOpen(false), []);

  const onClickInappropiateContent = useCallback(() => {
    setReportModalOpen(true);
  }, []);

  const [isReportModalOpen, setReportModalOpen] = useState(false);

  const closeReportModal = useCallback(() => {
    setReportModalOpen(false);
  }, []);

  const [type, setType] = useState<ViolationType>(
    ViolationType.VIOLATION_TYPE_RIGHTS
  );

  const [message, setMessage] = useState('');

  const [captchaToken, setCaptchaToken] = useState<string | undefined>(
    undefined
  );

  const submitDisabled = useMemo(
    () => message === '' || typeof captchaToken === 'undefined',
    [captchaToken, message]
  );

  const onChangeType = useCallback<React.ChangeEventHandler<HTMLSelectElement>>(
    (e) => {
      const { value } = e.target;
      setType(Number(value) as ViolationType);
    },
    []
  );

  const onChangeMessage = useCallback<
    React.ChangeEventHandler<HTMLTextAreaElement>
  >((e) => {
    const { value } = e.target;
    setMessage(value);
  }, []);

  const { notify } = useSystemNotification();

  const handleSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(
    async (e) => {
      e.preventDefault();

      if (typeof captchaToken === 'undefined') return;
      try {
        await apiRoutesFetch('/recaptcha/verify', {
          method: 'POST',
          body: JSON.stringify({ captchaToken }),
        });

        const reportType = (() => {
          switch (type) {
            case ViolationType.VIOLATION_TYPE_VIOLENCE:
              return types.ViolationType.VIOLATION_TYPE_VIOLENCE;
            case ViolationType.VIOLATION_TYPE_HARASSMENT:
              return types.ViolationType.VIOLATION_TYPE_HARASSMENT;
            case ViolationType.VIOLATION_TYPE_PRIVACY:
              return types.ViolationType.VIOLATION_TYPE_PRIVACY;
            case ViolationType.VIOLATION_TYPE_RIGHTS:
              return types.ViolationType.VIOLATION_TYPE_RIGHTS;
            case ViolationType.VIOLATION_TYPE_ADULT:
              return types.ViolationType.VIOLATION_TYPE_ADULT;
            case ViolationType.VIOLATION_TYPE_SPAM:
              return types.ViolationType.VIOLATION_TYPE_SPAM;
            case ViolationType.VIOLATION_TYPE_OTHER:
              return types.ViolationType.VIOLATION_TYPE_OTHER;
            case ViolationType.VIOLATION_TYPE_UNKNOWN:
              return types.ViolationType.VIOLATION_TYPE_UNKNOWN;
          }
        })();

        await reportStoryViolation({
          type: reportType,
          message,
          story_id: storyId,
        });

        notify('報告を受け付けました。内容を確認いたします。', 'info');
      } catch {
        notify('報告の送信に失敗しました。再度お試しください。', 'error');
      } finally {
        closeReportModal();
        close();
      }
    },
    [captchaToken, close, closeReportModal, message, notify, storyId, type]
  );

  return {
    isOpen,
    open,
    close,
    onClickInappropiateContent,
    isReportModalOpen,
    closeReportModal,
    submitDisabled,
    setCaptchaToken,
    onChangeType,
    onChangeMessage,
    handleSubmit,
  };
};
